import React, { useEffect, useMemo, useState } from 'react';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { getCreditPacks, purchaseCredits } from '../api/endpoints';
import type { CreditPack } from '../types/api';
import { useUser } from '../contexts/UserContext';
import Coin from './kivo/Coin';
import { getErrorMessage } from '../utils/errors';

function formatPrice(cents: number, currency: string): string {
  const amount = (cents / 100).toFixed(2);
  const symbol = currency === 'EUR' ? '€' : currency === 'USD' ? '$' : `${currency} `;
  return `${symbol}${amount}`;
}

/** Pick a sensible default pack (a "popular"/middle tier when available). */
function defaultPack(packs: CreditPack[]): string | null {
  if (packs.length === 0) return null;
  const sorted = [...packs].sort((a, b) => a.credits - b.credits);
  return (sorted[Math.min(1, sorted.length - 1)] ?? sorted[0]).id;
}

export interface TopUpSheetProps {
  /** Optional attempted-feature label + cost, shown in the header line. */
  featureLabel?: string | null;
  featureCost?: number | null;
  onClose: () => void;
  /** Called after a successful purchase with the pack that was bought. */
  onPurchased?: (pack: CreditPack) => void;
}

/**
 * Bottom-sheet credit top-up popup — the "microtransaction" moment. Mirrors the
 * Kivo out-of-credits paywall: a scrim, a rising sheet, the coin, a pack grid,
 * a buy CTA and a "maybe later" dismissal.
 *
 * Packs and prices are sourced from the API — never hardcoded.
 */
export default function TopUpSheet({
  featureLabel,
  featureCost,
  onClose,
  onPurchased,
}: TopUpSheetProps): React.JSX.Element {
  const { credits, refreshCredits } = useUser();
  const [packs, setPacks] = useState<CreditPack[] | null>(null);
  const [sel, setSel] = useState<string | null>(null);
  const [buying, setBuying] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    getCreditPacks()
      .then((p) => {
        if (!alive) return;
        const active = p.filter((x) => x.active);
        setPacks(active);
        setSel(defaultPack(active));
      })
      .catch(() => { if (alive) setPacks([]); });
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !buying) onClose();
    };
    window.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [buying, onClose]);

  const pack = useMemo(() => packs?.find((p) => p.id === sel) ?? null, [packs, sel]);
  const total = credits?.total ?? 0;

  const handleBuy = async () => {
    if (!pack) return;
    setBuying(true);
    setErr(null);
    try {
      await purchaseCredits(pack.id);
      await refreshCredits();
      onPurchased?.(pack);
      onClose();
    } catch (error_) {
      setErr(getErrorMessage(error_, 'Purchase failed. Please try again.'));
    } finally {
      setBuying(false);
    }
  };

  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 z-[200] flex items-end justify-center"
      role="dialog"
      aria-modal="true"
      aria-label="Add credits"
    >
      {/* scrim */}
      <motion.div
        className="absolute inset-0"
        style={{ background: 'rgba(20,24,22,0.42)', backdropFilter: 'blur(2px)' }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
        onClick={buying ? undefined : onClose}
      />

      {/* sheet */}
      <motion.div
        className="relative w-full"
        style={{
          maxWidth: 520,
          background: 'var(--c-cream)',
          borderRadius: '28px 28px 0 0',
          padding: '12px 20px calc(20px + env(safe-area-inset-bottom))',
          boxShadow: '0 -20px 60px rgba(31,36,33,.28)',
        }}
        initial={{ y: '100%' }}
        animate={{ y: 0 }}
        exit={{ y: '100%' }}
        transition={{ type: 'spring', stiffness: 420, damping: 40, mass: 0.9 }}
        onClick={(e) => { e.stopPropagation(); }}
      >
        {/* grabber */}
        <div
          style={{
            width: 40,
            height: 5,
            borderRadius: 99,
            background: 'var(--rule)',
            margin: '0 auto 16px',
          }}
        />

        {/* header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 11, marginBottom: 14 }}>
          <div
            style={{
              width: 42,
              height: 42,
              borderRadius: 13,
              background: 'var(--c-teal-weak)',
              color: 'var(--c-deep-teal)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <Coin size={22} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="t-h3" style={{ margin: 0, fontWeight: 500 }}>
              Add credits to continue
            </div>
            <div className="t-small t-mute" style={{ marginTop: 2 }}>
              {featureCost != null
                ? `${featureLabel ?? 'This action'} costs ${featureCost} — you have ${total}.`
                : "Pick a pack — you'll be right back to where you left off."}
            </div>
          </div>
        </div>

        {/* pack grid */}
        {packs == null ? (
          <div className="t-small t-mute" style={{ padding: '24px 4px' }}>
            Loading packs…
          </div>
        ) : packs.length === 0 ? (
          <div className="t-small t-mute" style={{ padding: '16px 4px' }}>
            No credit packs are available right now. Please try again later.
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            {packs
              .slice()
              .sort((a, b) => a.credits - b.credits)
              .map((p, i) => {
                const on = sel === p.id;
                const badge = i === 1 ? 'Popular' : i === packs.length - 1 && packs.length > 2 ? 'Best value' : null;
                return (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => { setSel(p.id); }}
                    style={{
                      position: 'relative',
                      textAlign: 'left',
                      cursor: 'pointer',
                      padding: '13px 13px',
                      borderRadius: 16,
                      background: on ? 'var(--c-teal-weak)' : 'var(--c-cream-weak)',
                      border: `1.5px solid ${on ? 'var(--c-kivo-teal)' : 'var(--rule)'}`,
                      transition: 'all .2s cubic-bezier(.22,.61,.36,1)',
                      boxShadow: on ? '0 6px 18px rgba(42,164,166,.20)' : 'none',
                    }}
                  >
                    {badge && (
                      <span
                        style={{
                          position: 'absolute',
                          top: -8,
                          right: 10,
                          font: '500 8px/1 var(--f-mono, monospace)',
                          letterSpacing: '.1em',
                          textTransform: 'uppercase',
                          background: badge === 'Best value' ? 'var(--c-deep-teal)' : 'var(--c-coral)',
                          color: '#fff',
                          padding: '4px 7px',
                          borderRadius: 999,
                        }}
                      >
                        {badge}
                      </span>
                    )}
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6,
                        color: 'var(--c-deep-teal)',
                        marginBottom: 6,
                      }}
                    >
                      <Coin size={16} />
                      <span style={{ font: '600 17px/1 var(--f-text)', color: 'var(--ink)' }}>
                        {p.credits.toLocaleString()}
                      </span>
                    </div>
                    <div
                      className="t-mute"
                      style={{
                        font: '400 11px/1 var(--f-text)',
                        textTransform: 'uppercase',
                        letterSpacing: '.08em',
                        marginBottom: 9,
                      }}
                    >
                      credits
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <span style={{ font: '500 15px/1 var(--f-display)', color: 'var(--ink)' }}>
                        {formatPrice(p.price_cents, p.currency)}
                      </span>
                      <span
                        style={{
                          width: 19,
                          height: 19,
                          borderRadius: '50%',
                          border: `1.5px solid ${on ? 'var(--c-kivo-teal)' : 'var(--rule)'}`,
                          background: on ? 'var(--c-kivo-teal)' : 'transparent',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#fff',
                        }}
                      >
                        {on && (
                          <svg width="11" height="11" viewBox="0 0 24 24" fill="none">
                            <path
                              d="M5 13l4 4L19 7"
                              stroke="currentColor"
                              strokeWidth="2.4"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        )}
                      </span>
                    </div>
                  </button>
                );
              })}
          </div>
        )}

        <AnimatePresence>
          {err && (
            <motion.p
              className="t-small"
              style={{ color: 'var(--c-error)', margin: '12px 2px 0' }}
              initial={{ opacity: 0, y: -4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
            >
              {err}
            </motion.p>
          )}
        </AnimatePresence>

        {/* buy */}
        <button
          type="button"
          disabled={!pack || buying}
          onClick={handleBuy}
          style={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 8,
            padding: '15px',
            marginTop: 16,
            borderRadius: 15,
            background: 'var(--c-deep-teal)',
            color: '#fff',
            border: 'none',
            cursor: pack && !buying ? 'pointer' : 'default',
            opacity: pack && !buying ? 1 : 0.6,
            font: '500 15px/1 var(--f-text)',
            boxShadow: '0 10px 24px rgba(42,164,166,.32)',
          }}
        >
          {buying
            ? 'Processing…'
            : pack
            ? `Buy ${pack.credits.toLocaleString()} · ${formatPrice(pack.price_cents, pack.currency)} & continue`
            : 'Buy credits'}
        </button>
        <button
          type="button"
          disabled={buying}
          onClick={onClose}
          style={{
            width: '100%',
            padding: '13px',
            marginTop: 8,
            borderRadius: 15,
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            font: '500 13px/1 var(--f-text)',
            color: 'var(--ink-muted)',
          }}
        >
          Maybe later
        </button>

        <div
          className="t-mute"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 6,
            marginTop: 10,
            font: '400 11px/1 var(--f-text)',
          }}
        >
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
            <rect x="4" y="10" width="16" height="10" rx="2" stroke="currentColor" strokeWidth="1.8" />
            <path d="M8 10V7a4 4 0 0 1 8 0v3" stroke="currentColor" strokeWidth="1.8" />
          </svg>
          Secure payment · credits never expire
        </div>
      </motion.div>
    </div>,
    document.body,
  );
}
